import {
    Component,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import {
    ColDef,
    GetDetailRowDataParams,
    GetRowIdParams,
    GridApi,
    GridOptions,
    GridReadyEvent,
    ICellRendererParams,
    IRowNode,
    IServerSideDatasource,
    IServerSideGetRowsRequest,
} from 'ag-grid-community';
import { first, take } from 'rxjs';

import { RouterService } from '@services/router.service';
import { UserService } from '@services/api/user/user.service';
import { UserDataService } from '@services/data/user-data.service';
import { StationColumnOptionsService } from '@services/station-column-options.service';
import { StationDocumentService } from '@services/api/station-document/station-document.service';
import { StationDiscrepancyService } from '@services/api/station-discrepancy/station-discrepancy.service';
import { WorkflowService } from '@services/api/workflow/workflow.service';
import { TableComponent } from '@grid-table/table.component';
import { TextFilterComponent } from '@grid-table/filter/text-filter/text-filter.component';
import { DateFilterComponent } from '@grid-table/filter/date-filter/date-filter.component';
import { GrayBoxCellComponent } from '@grid-table/cell/gray-box-cell/gray-box-cell.component';
import { PriorityCellComponent } from '@grid-table/cell/priority-cell/priority-cell.component';
import { SelectFilterComponent } from '@grid-table/filter/select-filter/select-filter.component';
import { ClickableCellComponent } from '@grid-table/cell/clickable-cell/clickable-cell.compoent';
import { CorrectionCellComponent } from '@grid-table/cell/correction-cell/correction-cell.component';
import { NoRowsOverlayComponent } from '@grid-table/overlay/no-rows-overlay/no-rows-overlay.component';
import { NotificationCellComponent } from '@grid-table/cell/notification-cell/notification-cell.component';
import {
    getFilterQuery,
    getPageIndex,
    getSortQuery,
    hideHiddenColumns,
    isFilterApplied,
    setFilterAndSorting,
} from '@helpers/table';
import {
    customColumnsToColDefs,
    customColumnsToColumnOptions,
} from '@helpers/custom-column';
import { pickFromEnum } from '@helpers/index';
import { SubscriptionList, SubscriptionListType } from '@helpers/subscription';
import {
    DocumentFilterOptions,
    StationDocument,
} from '@models/station-document';
import { CustomColumn } from '@models/workflow/custom-column';
import { CountInformation } from '@models/shared/count-information';
import { IdDisplayPair } from '@models/shared.model';
import { UserInfo } from '@models/user';
import { ExportFileNames } from '@constants/table';
import {
    InboxTab,
    DocumentColumn,
    DocumentNestedColumn,
    getDaysSinceNotification,
} from '@station/utils';
import DATA_QA from '@automation/data-qa.json';

@Component({
    selector: 'app-station-document-view',
    templateUrl: 'document-view.component.html',
    imports: [TableComponent],
})
export class StationDocumentViewComponent
    implements OnInit, OnChanges, OnDestroy
{
    @Input() inboxTab: InboxTab | null = null;
    @Input() workflowId: number | undefined;
    @Output() customColumnsChange = new EventEmitter<CustomColumn[]>();

    filterApplied: boolean = false;
    resultCount: number = 0;
    countInformation = CountInformation.initial();
    lastSyncDate: Date | undefined;
    protected error = false;
    protected readonly dataQa = DATA_QA;
    protected columnDefs: ColDef[] = [];
    protected gridOptions!: GridOptions;
    protected detailCellRendererParams = () =>
        this.getDetailCellRendererParams();
    private _gridApi!: GridApi;
    private hiddenColumnOptions: string[] = [];
    private hiddenNestedColumnOptions: Set<string> = new Set([]);
    private customColumns: CustomColumn[] = [];
    private _subscriptions = new SubscriptionList() as SubscriptionListType;

    private readonly discrepancyService = inject(StationDiscrepancyService);
    private readonly documentService = inject(StationDocumentService);
    private readonly columnOptionsService = inject(StationColumnOptionsService);
    private readonly userDataService = inject(UserDataService);
    private readonly userService = inject(UserService);
    private readonly routerService = inject(RouterService);
    private readonly workflowService = inject(WorkflowService);

    ngOnInit(): void {
        this.setGridOptions();
        this.setColumnDefs();
        this.handleColumnOptions();
        this.fetchCustomColumns();
        this.getFilterOptions();
    }

    ngOnChanges(changes: SimpleChanges): void {
        const inboxTabChange = changes['inboxTab'];
        const workflowIdChange = changes['workflowId'];

        if (inboxTabChange && !inboxTabChange.firstChange) {
            this.resetFilters();
            this.getFilterOptions(true);
        }
        if (workflowIdChange && !workflowIdChange.firstChange) {
            this.resetFilters();
            this.fetchCustomColumns(true);
            this.getFilterOptions(true);
        }
    }

    ngOnDestroy() {
        this._subscriptions['get-inbox-choices'] = this.userService
            .getStationInboxChoices()
            .subscribe({
                complete: () => {
                    this._subscriptions.unsubscribeAllSafe();
                },
            });
    }

    setGridOptions() {
        const date = new Date().toUTCString();
        const fileNameToExport = `${ExportFileNames.INBOX} - ${date}`;

        this.gridOptions = {
            keepDetailRows: true,
            enableRangeSelection: true,
            defaultExcelExportParams: { fileName: fileNameToExport },
            defaultCsvExportParams: { fileName: fileNameToExport },
        };
    }
    setColumnDefs(filterOptions = DocumentFilterOptions.initial()) {
        this.columnDefs = [...this.getColumnDefs(filterOptions)];
    }

    onGridReady(event: GridReadyEvent): void {
        this._gridApi = event.api;

        const datasource = this.getDocuments();
        this._gridApi.setGridOption('serverSideDatasource', datasource);
        this.applySavedColumnVisibility();
    }

    resetFilters() {
        if (!this._gridApi.isDestroyed()) {
            this._gridApi?.refreshHeader();
            this._gridApi?.setFilterModel(null);
            this._gridApi?.onFilterChanged();
        }
    }

    refreshData(purge = true) {
        if (!this._gridApi.isDestroyed()) {
            if (this.error) {
                const datasource = this.getDocuments();
                this._gridApi.setGridOption('serverSideDatasource', datasource);
            } else {
                this._gridApi.refreshServerSide({ purge });
            }
        }
    }

    private getFilterOptions(ignoreUserChoices = false, refreshAfter = false) {
        this.documentService
            .getStationDocumentsFilterOptions(this.buildBaseParams())
            .pipe(first())
            .subscribe({
                next: (response) => {
                    this.setColumnDefs(response);
                    setTimeout(() => this.applySavedColumnVisibility());
                    if (refreshAfter) this.refreshData();
                },
                complete: () => {
                    if (!ignoreUserChoices) this.setSavedFilter();
                },
            });
    }

    private fetchCustomColumns(refreshAfter = false) {
        if (this.workflowId === undefined || this.workflowId === null) {
            this.customColumns = [];
            this.customColumnsChange.emit([]);
            this.detailCellRendererParams = () =>
                this.getDetailCellRendererParams();
            if (refreshAfter) this.refreshData();
            return;
        }
        this._subscriptions.unsubscribeSafe('custom-columns');
        this._subscriptions['custom-columns'] = this.workflowService
            .getCustomColumns(this.workflowId)
            .pipe(first())
            .subscribe({
                next: (columns: CustomColumn[]) => {
                    this.customColumns = columns;
                    this.customColumnsChange.emit(columns);

                    this.detailCellRendererParams = () =>
                        this.getDetailCellRendererParams();
                    if (refreshAfter) this.refreshData();
                },
            });
    }

    private buildBaseParams(): Record<string, any> {
        const params: Record<string, any> = {
            inboxTab: this.inboxTab ?? undefined,
        };
        if (this.workflowId !== undefined && this.workflowId !== null) {
            params['workflowId'] = this.workflowId;
        }
        return params;
    }

    private setSavedFilter() {
        this._subscriptions['set-saved-filter'] =
            this.userDataService.stationUserInboxChoices$.subscribe({
                next: (choices) => {
                    if (choices) {
                        setFilterAndSorting(
                            this._gridApi,
                            pickFromEnum(choices.document, {
                                ...DocumentColumn,
                                order: 'order',
                                orderBy: 'orderBy',
                            }),
                        );
                        this.columnOptionsService.syncColumnConfig(
                            choices.columnConfig,
                        );
                    }
                },
            });
    }

    private getColumnDefs(filterOptions: DocumentFilterOptions): ColDef[] {
        return [
            {
                field: DocumentColumn.NAME,
                headerName: 'Document Name',
                showRowGroup: true,
                filter: 'agTextColumnFilter',
                floatingFilterComponent: TextFilterComponent,
                cellRenderer: 'agGroupCellRenderer',
                cellRendererParams: {
                    suppressCount: true,
                    suppressDoubleClickExpand: true,
                    innerRenderer: ClickableCellComponent,
                    innerRendererParams: {
                        dataQa: this.dataQa.stationDocumentViewDocumentNameCell,
                        onClick: (node: IRowNode) =>
                            this.goDocumentDetails(node.data.id),
                        tooltipGetter: (params: ICellRendererParams) =>
                            params.data.name,
                    },
                },
            },
            {
                field: DocumentColumn.DOC_TYPE,
                filter: 'agTextColumnFilter',
                floatingFilterComponent: SelectFilterComponent,
                floatingFilterComponentParams: {
                    suppressFloatingFilterButton: true,
                    selectList: filterOptions.documentType,
                    autocomplete: true,
                },
            },
            {
                field: DocumentColumn.PRIORITY,
                cellRenderer: PriorityCellComponent,
                filter: 'agTextColumnFilter',
                floatingFilterComponent: SelectFilterComponent,
                floatingFilterComponentParams: {
                    suppressFloatingFilterButton: true,
                    selectList: filterOptions.priority,
                    priority: true,
                },
            },
            {
                field: DocumentColumn.NOTIFY_DATE,
                filter: 'agTextColumnFilter',
                floatingFilterComponent: DateFilterComponent,
            },
            {
                field: DocumentColumn.STATION,
                headerName: 'Station',
                minWidth: 120,
                filter: 'agTextColumnFilter',
                floatingFilterComponent: SelectFilterComponent,
                floatingFilterComponentParams: {
                    suppressFloatingFilterButton: true,
                    selectList: filterOptions.station,
                    autocomplete: true,
                    noneOption: true,
                },
            },
        ];
    }

    private goDocumentDetails(id: number, discrepancyId?: number) {
        this.routerService.goStationDocumentDetails(id, discrepancyId);
    }

    private getDocuments(): IServerSideDatasource {
        this._subscriptions.unsubscribeSafe('get-documents');
        this.error = false;

        return {
            getRows: (params) => {
                params.api.hideOverlay();
                this.filterApplied = isFilterApplied(params.request);
                this.getCount(this.getQueryParams(params.request));

                this.documentService
                    .getStationDocuments(this.getQueryParams(params.request))
                    .pipe(first())
                    .subscribe({
                        next: (data) => {
                            this.lastSyncDate = new Date();
                            params.api.hideOverlay();

                            if (data) {
                                params.success({
                                    rowData: data.items,
                                    rowCount: data.totalItems ?? -1,
                                });
                                if (data.items.length === 0) {
                                    params.api.showNoRowsOverlay();
                                }
                                this.resultCount = data.totalItems ?? 0;
                            } else {
                                params.fail();
                            }
                        },
                        error: () => {
                            params.api.hideOverlay();
                            this.error = true;
                            this.lastSyncDate = undefined;
                        },
                    });
            },
        };
    }

    private getCount(params: { pageIndex: number; pageSize: number }) {
        const { pageIndex, pageSize, ...rest } = params;

        this.documentService
            .getStationDocumentsCount(rest)
            .pipe(take(1))
            .subscribe({
                next: (response) => {
                    if (response) {
                        this.countInformation = response;
                    }
                },
            });
    }

    private getQueryParams(request: IServerSideGetRowsRequest) {
        const pageSize = this._gridApi.paginationGetPageSize();
        const pageIndex = getPageIndex(request, pageSize);

        return {
            ...this.buildBaseParams(),
            ...getFilterQuery(request.filterModel),
            ...getSortQuery(request.sortModel),
            pageIndex,
            pageSize,
        };
    }

    private getDetailCellRendererParams() {
        return {
            detailGridOptions: this.getDetailGridOptions(),
            getDetailRowData: (params: GetDetailRowDataParams) =>
                this.getDiscrepancies(params),
        };
    }

    private getDetailGridOptions() {
        return {
            rowHeight: 35,
            headerHeight: 32,
            enableRangeSelection: true,
            columnDefs: this.getDetailColumnDefs(),
            getRowId: (params: GetRowIdParams) => 'nested-' + params.data.id,
            noRowsOverlayComponent: NoRowsOverlayComponent,
            noRowsOverlayComponentParams: {
                text: 'No discrepancies found',
                isDetail: true,
            },
            defaultColDef: {
                flex: 1,
                cellStyle: { display: 'flex', alignItems: 'center' },
                minWidth: 100,
            },
        };
    }

    private getDetailColumnDefs(): ColDef[] {
        const columnDefs = [
            {
                field: DocumentNestedColumn.ID,
                headerName: 'Discrepancy ID',
                cellRenderer: ClickableCellComponent,
                cellRendererParams: {
                    onClick: (node: IRowNode) =>
                        this.goDocumentDetails(
                            node.data.documentId,
                            node.data.id,
                        ),
                },
            },
            {
                field: DocumentNestedColumn.REASON,
                valueFormatter: (p: { value: IdDisplayPair }) =>
                    p.value.displayName,
            },
            {
                field: DocumentNestedColumn.PRIORITY,
                maxWidth: 130,
                cellRenderer: PriorityCellComponent,
            },
            {
                field: DocumentNestedColumn.NOTIFICATION,
                minWidth: 170,
                cellRenderer: NotificationCellComponent,
            },
            {
                field: DocumentNestedColumn.CORRECTION,
                cellRenderer: CorrectionCellComponent,
            },
            {
                field: DocumentNestedColumn.NOTIFY_DATE,
                headerName: 'Notify Date',
                maxWidth: 160,
            },
            {
                field: DocumentNestedColumn.DAYS_SINCE_NOTIFICATION,
                headerName: 'Days Notified',
                cellRenderer: GrayBoxCellComponent,
                valueFormatter: getDaysSinceNotification,
            },
            {
                field: DocumentNestedColumn.STATION,
                headerName: 'Station',
            },
            {
                field: DocumentNestedColumn.PRIMARY_FIXER,
                valueFormatter: (p: { value: UserInfo }) => p.value.fullName,
                resizable: false,
            },
            ...customColumnsToColDefs(
                this.customColumns,
                Array.from(this.hiddenNestedColumnOptions),
            ),
        ];

        return columnDefs.filter((c: ColDef) => {
            const key = (c.colId ?? c.field) as string | undefined;
            return (
                !key || !this.hiddenNestedColumnOptions.has(key.toLowerCase())
            );
        });
    }

    private getDiscrepancies(params: GetDetailRowDataParams<StationDocument>) {
        const documentId = params.data.id;

        this.discrepancyService
            .getDiscrepanciesByDocumentId(documentId, {
                ...this.buildBaseParams(),
            })
            .pipe(take(1))
            .subscribe({
                next: (response) => {
                    params.successCallback(response.discrepancies);
                },
            });
    }

    private handleColumnOptions() {
        this._subscriptions['column-options'] =
            this.columnOptionsService.columnOptions$.subscribe({
                next: (options) => {
                    this.hiddenColumnOptions = options.hiddenColumns;
                    this.applySavedColumnVisibility();
                    const option = options.lastToggled;

                    if (
                        option &&
                        this._gridApi &&
                        !this._gridApi.isDestroyed()
                    ) {
                        this._gridApi.setColumnsVisible(
                            [option.id],
                            option.checked,
                        );
                    }
                },
            });

        this._subscriptions['nested-column-options'] =
            this.columnOptionsService.nestedColumnOptions$.subscribe({
                next: (options: { hiddenColumns: string[] }) => {
                    this.hiddenNestedColumnOptions = new Set(
                        options.hiddenColumns.map((id) => id.toLowerCase()),
                    );
                    this.detailCellRendererParams = () =>
                        this.getDetailCellRendererParams();
                },
            });
    }

    private applySavedColumnVisibility() {
        hideHiddenColumns(this._gridApi, this.hiddenColumnOptions);
    }
}
